import { DefaultEffects, getTheme, Stack } from '@fluentui/react';
import React, { useRef, useState } from 'react';
import { IStatusAction } from '../../helpers/userHooks/userTypes';
import CircularActionButton from './CircularActionButton';
import AwayOptionsMenu from './AwayOptionsMenu';
import AtWorkOptionsMenu from './AtWorkOptionsMenu';
import CustomAtWorkMessageModal from './CustomAtWorkMessageModal';
import FocusTimeModal from './FocusTimeModal';
import OutOfOfficeModal from './OutOfOfficeModal';
import type { IOutOfOfficePreferredMethods } from 'shared-state/identity/atoms';
import { clearIdleChainStage } from 'features/InactivityRules/inactivityStorage';
import { acknowledgeOooStatusReminder } from 'features/Rooms/RoomList/helpers/oooStatusReminder';
import {
    useUserStatus,
    useAtWorkLocation,
    useAtWorkCustomPhrase,
    useAwayStatusMessage,
    useAwaySinceTimestamp,
    useFocusEndTime,
    useFocusDurationMinutes,
    useFocusTimeKnockDoor,
    useFocusTimeTextMessage,
    useOutOfOfficeReturnDate,
    useOutOfOfficeReturnTime,
    useOutOfOfficeAbsenceComment,
    useOutOfOfficePreferredMethods
} from 'shared-state/identity/hooks';
import OnCallModal from './OnCallModal';
import { AtWorkLocation } from 'shared-state/identity/types';

export interface IStatusActionsHoverCardProps {
    actions: IStatusAction[];
    onCloseIcons?: () => void;
}

/**
 * Horizontal action panel displayed to the right of the profile picture.
 * Contains circular action buttons arranged horizontally.
 * Away action opens a submenu with predefined options.
 */
const StatusActionsHoverCard: React.FC<IStatusActionsHoverCardProps> = ({ actions, onCloseIcons }) => {
    const theme = getTheme();
    const [awayMenuOpen, setAwayMenuOpen] = useState(false);
    const [atWorkMenuOpen, setAtWorkMenuOpen] = useState(false);
    const [customAtWorkModalOpen, setCustomAtWorkModalOpen] = useState(false);
    const [focusTimeModalOpen, setFocusTimeModalOpen] = useState(false);
    const [onCallModalOpen, setOnCallModalOpen] = useState(false);
    const [oooModalOpen, setOooModalOpen] = useState(false);
    const awayButtonRef = useRef<HTMLDivElement>(null);
    const atWorkButtonRef = useRef<HTMLDivElement>(null);
    const [, setCurrentStatus] = useUserStatus();
    const [atWorkLocation, setAtWorkLocation] = useAtWorkLocation();
    const [, setAtWorkCustomPhrase] = useAtWorkCustomPhrase();
    const [, setAwayStatusMessage] = useAwayStatusMessage();
    const [, setFocusEndTime] = useFocusEndTime();
    const [, setFocusDurationMinutes] = useFocusDurationMinutes();
    const [, setFocusTimeKnockDoor] = useFocusTimeKnockDoor();
    const [, setFocusTimeTextMessage] = useFocusTimeTextMessage();
    const [, setOutOfOfficeReturnDate] = useOutOfOfficeReturnDate();
    const [, setOutOfOfficeReturnTime] = useOutOfOfficeReturnTime();
    const [, setOutOfOfficeAbsenceComment] = useOutOfOfficeAbsenceComment();
    const [, setOutOfOfficePreferredMethods] = useOutOfOfficePreferredMethods();
    const [, setAwaySinceTimestamp] = useAwaySinceTimestamp();

    const handleAwayMenuOpen = () => {
        setAwayMenuOpen(true);
    };

    const handleAwayMenuClose = () => {
        setAwayMenuOpen(false);
    };

    const handleAtWorkMenuOpen = () => {
        setAtWorkMenuOpen(true);
    };

    const handleAtWorkMenuClose = () => {
        setAtWorkMenuOpen(false);
    };

    const handleAtWorkOptionSelect = (location: AtWorkLocation) => {
        setCurrentStatus('available');
        setAtWorkLocation(location);
        if (location !== 'custom') {
            setAtWorkCustomPhrase(undefined);
        }
        onCloseIcons?.();
        localStorage.removeItem('isUserSelectedAwayManually');
        localStorage.removeItem('isUserSelectedOooManually');
        clearIdleChainStage();
        setAwaySinceTimestamp(undefined);
    };

    const handleAtWorkCustomPhraseSelect = (phrase: string) => {
        setCurrentStatus('available');
        setAtWorkLocation('custom');
        setAtWorkCustomPhrase(phrase);
        setCustomAtWorkModalOpen(false);
        onCloseIcons?.();
        localStorage.removeItem('isUserSelectedAwayManually');
        localStorage.removeItem('isUserSelectedOooManually');
        clearIdleChainStage();
        setAwaySinceTimestamp(undefined);
    };

    const handleOpenCustomAtWorkModal = () => {
        setCustomAtWorkModalOpen(true);
    };

    const handleAwayOptionSelect = (message: string) => {
        setCurrentStatus('away');
        setAwayStatusMessage(message);
        onCloseIcons?.();
        localStorage.setItem('isUserSelectedAwayManually', 'true');
        clearIdleChainStage();
        localStorage.removeItem('isUserSelectedOooManually');
        setAwaySinceTimestamp(Date.now());
    };

    const handleFocusTimeModalOpen = () => {
        setFocusTimeModalOpen(true);
    };

    const handleFocusTimeModalClose = () => {
        setFocusTimeModalOpen(false);
    };

    const handleFocusTimeDone = (_endTimeTimestamp: number, durationMinutes: number, knockDoor: boolean, textMessage: boolean) => {
        setCurrentStatus('busy');
        setFocusDurationMinutes(durationMinutes);
        setFocusEndTime(Date.now() + durationMinutes * 60 * 1000);
        setFocusTimeKnockDoor(knockDoor);
        setFocusTimeTextMessage(textMessage);
        setFocusTimeModalOpen(false);
        onCloseIcons?.();
        localStorage.removeItem('isUserSelectedAwayManually');
        localStorage.removeItem('isUserSelectedOooManually');
        clearIdleChainStage();
        setAwaySinceTimestamp(undefined);
    };

    const handleOnCallModalOpen = () => {
        setOnCallModalOpen(true);
    };

    const handleOnCallConfirm = (durationMinutes: number) => {
        setCurrentStatus('on-the-phone');
        setFocusDurationMinutes(durationMinutes);
        setFocusEndTime(Date.now() + durationMinutes * 60 * 1000);
        setOnCallModalOpen(false);
        onCloseIcons?.();
        localStorage.removeItem('isUserSelectedAwayManually');
        localStorage.removeItem('isUserSelectedOooManually');
        clearIdleChainStage();
        setAwaySinceTimestamp(undefined);
    };

    const handleOnCallCancel = () => {
        setCurrentStatus('available');
        setOnCallModalOpen(false);
        localStorage.removeItem('isUserSelectedAwayManually');
        localStorage.removeItem('isUserSelectedOooManually');
        clearIdleChainStage();
        setAwaySinceTimestamp(undefined);
    };

    const handleOooModalOpen = () => {
        setOooModalOpen(true);
    };

    const handleOooModalClose = () => {
        setOooModalOpen(false);
    };

    const handleOooConfirm = (payload: {
        returnDate: string;
        returnTime: string | null;
        absenceComment: string;
        preferredMethods: IOutOfOfficePreferredMethods;
    }) => {
        setCurrentStatus('out-of-office');
        // Convert string date back to Date object for state management
        setOutOfOfficeReturnDate(new Date(payload.returnDate));
        setOutOfOfficeReturnTime(payload.returnTime);
        setOutOfOfficeAbsenceComment(payload.absenceComment);
        setOutOfOfficePreferredMethods(payload.preferredMethods);
        setOooModalOpen(false);
        onCloseIcons?.();
        localStorage.removeItem('isUserSelectedAwayManually');
        localStorage.setItem('isUserSelectedOooManually', 'true');
        acknowledgeOooStatusReminder();
        clearIdleChainStage();
        setAwaySinceTimestamp(undefined);
    };

    return (
        <>
            <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 4 }}
                style={{
                    padding: '4px 8px',
                    borderRadius: 20,
                    boxShadow: DefaultEffects.elevation16,
                    background: theme.palette.white,
                    whiteSpace: 'nowrap'
                }}
            >
                {actions.map((action) => (
                    <CircularActionButton
                        key={action.key}
                        ref={action.isAway ? awayButtonRef : action.isAvailable ? atWorkButtonRef : undefined}
                        action={action}
                        onCustomClick={
                            action.isAvailable
                                ? handleAtWorkMenuOpen
                                : action.isAway
                                ? handleAwayMenuOpen
                                : action.isBusy
                                ? handleFocusTimeModalOpen
                                : action.isOnThePhone
                                ? handleOnCallModalOpen
                                : action.isOutOfOffice
                                ? handleOooModalOpen
                                : undefined
                        }
                    />
                ))}
            </Stack>

            {/* Away Options Menu */}
            <AwayOptionsMenu isOpen={awayMenuOpen} targetRef={awayButtonRef} onOptionSelect={handleAwayOptionSelect} onClose={handleAwayMenuClose} />

            {/* At Work Options Menu */}
            <AtWorkOptionsMenu
                isOpen={atWorkMenuOpen}
                targetRef={atWorkButtonRef}
                selectedLocation={atWorkLocation}
                onOptionSelect={handleAtWorkOptionSelect}
                onOpenCustomModal={handleOpenCustomAtWorkModal}
                onClose={handleAtWorkMenuClose}
            />

            {/* Custom At Work Message Modal - rendered in parent so it persists when menu closes */}
            <CustomAtWorkMessageModal
                isOpen={customAtWorkModalOpen}
                onConfirm={handleAtWorkCustomPhraseSelect}
                onDismiss={() => setCustomAtWorkModalOpen(false)}
            />

            {/* Focus Time (Do Not Disturb) Modal - status set only on Done */}
            <FocusTimeModal isOpen={focusTimeModalOpen} onDone={handleFocusTimeDone} onDismiss={handleFocusTimeModalClose} />
            {/* On call modal - select duration, Confirm = on-the-phone + timer, Cancel = available */}
            <OnCallModal isOpen={onCallModalOpen} onConfirm={handleOnCallConfirm} onCancel={handleOnCallCancel} onDismiss={handleOnCallCancel} />
            {/* Out of Office modal */}
            <OutOfOfficeModal isOpen={oooModalOpen} onConfirm={handleOooConfirm} onDismiss={handleOooModalClose} />
        </>
    );
};

export default StatusActionsHoverCard;
